/**
 * chunkDateRange — split an inclusive [startISO, endISO] date range into
 * consecutive windows no longer than `maxDays` days each.
 *
 * AK-195: the satellite-dates backend hard-caps a single request window at
 * `MAX_WINDOW_DAYS = 90` (NFR-6, returns 422 above that). A full season
 * (~apr–oct ≈ 214 days) therefore must be fetched in chunks. We default to
 * 89 days to stay safely under the inclusive 90-day cap (`to - from <= 90`).
 *
 * Each window is inclusive on both ends; consecutive windows do not overlap
 * (next.from = prev.to + 1 day). Returns [] for invalid/empty/reversed ranges.
 */
export interface DateWindow {
  from: string;
  to: string;
}

const DAY_MS = 86_400_000;

function toISODate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

export function chunkDateRange(
  startISO: string,
  endISO: string,
  maxDays = 89,
): DateWindow[] {
  const start = Date.parse(`${startISO}T00:00:00Z`);
  const end = Date.parse(`${endISO}T00:00:00Z`);
  if (Number.isNaN(start) || Number.isNaN(end) || start > end || maxDays < 1) {
    return [];
  }

  const windows: DateWindow[] = [];
  let cursor = start;
  while (cursor <= end) {
    const to = Math.min(cursor + maxDays * DAY_MS, end);
    windows.push({ from: toISODate(cursor), to: toISODate(to) });
    cursor = to + DAY_MS;
  }
  return windows;
}
